import { keepPreviousData, useInfiniteQuery } from "@tanstack/react-query";

import { ACCOUNT_USERS_PER_PAGE } from "@/api/graphql/accounts/getAccountUsers/constants.ts";
import { getUserCompanies } from "@/api/graphql/accounts/getUserCompanies/api.ts";
import { toUserCompaniesBO } from "@/api/graphql/accounts/getUserCompanies/mappers.ts";
import { DEFAULT_FIRST_PAGE } from "@/api/graphql/agents/search";
import { queries } from "@/api/graphql/constants.ts";
import { getNextPageParam } from "@/helpers/api/getNextPageParam";

type UseUserCompaniesOptions = {
  paginateResponse?: boolean;
};

export const useUserCompanies = (
  userId: string,
  searchTerm?: string,
  { paginateResponse }: UseUserCompaniesOptions = {},
) => {
  const filters = {
    userId: userId,
    query: {
      account: searchTerm?.length && searchTerm?.length > 2 ? searchTerm : "",
    },
  };
  const response = useInfiniteQuery({
    initialPageParam: DEFAULT_FIRST_PAGE,
    placeholderData: keepPreviousData,
    queryKey: [queries.GET_USER_COMPANIES, { userId, searchTerm }],
    queryFn: async ({ pageParam = DEFAULT_FIRST_PAGE }) => {
      try {
        return await getUserCompanies({
          filters: filters,
          ...(paginateResponse && {
            paging: {
              page: pageParam,
              size: ACCOUNT_USERS_PER_PAGE,
            },
          }),
        });
      } catch (_err) {
        return;
      }
    },
    getNextPageParam: (lastPage, allPages) =>
      getNextPageParam({
        lastPage,
        allPages,
        key: "AccountsMsSearchPrivileges",
      }),
  });

  return {
    data: response?.data ? toUserCompaniesBO(response.data) : undefined,
    error: response.error,
    isLoading: response.isLoading,
    fetchNextPage: response.fetchNextPage,
    hasNextPage: response.hasNextPage,
    totalUserCompanies:
      response.data?.pages[0]?.AccountsMsSearchPrivileges.pagination.total ?? 0,
  };
};
